import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DAY_NAMES, MONTH_NAMES } from './utils';

const LR_GREEN = '#7bb23f';
const SEP = '; ';

interface WeekInfo { week: number; year: number; dates: string[]; }
interface Worker { id: string; Title: string; FullName: string; SortOrder: number; IsActive: boolean; }
interface Car { id: string; Title: string; Color: string; TextColor?: string; SortOrder: number; Notes?: string; }
interface Entry { WorkerName: string; EntryDate: string; ProjectNames: string; CarId: number; }
interface ApiData { weeks: WeekInfo[]; workers: Worker[]; cars: Car[]; entries: Entry[]; }

function fmtDate(dateStr: string): string {
  const [, m, d] = dateStr.split('-');
  return `${parseInt(d)}.${parseInt(m)}.`;
}

export default function WorkerView() {
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year] = useState(today.getFullYear());
  const [data, setData] = useState<ApiData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const topbarRef = useRef<HTMLDivElement>(null);
  const [stickyTop, setStickyTop] = useState(0);

  const todayStr = today.toISOString().slice(0, 10);

  const loadData = useCallback(async (m: number, y: number, silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await fetch(`/api/workers?month=${m}&year=${y}`);
      if (!r.ok) throw new Error(`API error: ${r.status}`);
      const json: ApiData = await r.json();
      setData(json);
      setLastRefresh(new Date());
      setError(null);
    } catch (e: any) {
      setError(e.message || String(e));
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData(month, year);
    const id = setInterval(() => loadData(month, year, true), 60000);
    return () => clearInterval(id);
  }, [loadData, month, year]);

  useEffect(() => {
    const el = topbarRef.current;
    if (!el) return;
    const measure = () => setStickyTop(el.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#f4f4f4' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: LR_GREEN, marginBottom: 12 }}>Lee Row s.r.o.</div>
          <div style={{ fontSize: 14, color: '#888' }}>Načítám data…</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#f4f4f4' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#e74c3c', marginBottom: 8 }}>Chyba načítání</div>
          <div style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>{error}</div>
          <button onClick={() => loadData(month, year)} style={{ padding: '8px 20px', background: LR_GREEN, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
            Zkusit znovu
          </button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const entryMap: Record<string, Entry> = {};
  data.entries.forEach(e => { entryMap[`${e.WorkerName}|${e.EntryDate}`] = e; });

  return (
    <div style={{ fontFamily: 'Segoe UI, Arial, sans-serif', minHeight: '100vh', background: '#f4f4f4', '--sticky-top': `${stickyTop}px` } as React.CSSProperties}>

      <div ref={topbarRef} style={{ position: 'sticky', top: 0, zIndex: 100 }}>
      {/* Header */}
      <div style={{ background: LR_GREEN, color: '#fff', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 16 }}>
        <span style={{ fontWeight: 700, fontSize: 20, letterSpacing: 1 }}>Lee Row s.r.o.</span>
        <span style={{ fontSize: 15, opacity: 0.85 }}>Přehled pracovníků</span>
        {lastRefresh && (
          <span style={{ marginLeft: 'auto', fontSize: 12, opacity: 0.75 }}>
            Aktualizováno {lastRefresh.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })}
          </span>
        )}
      </div>

      {/* Month tabs */}
      <div style={{ display: 'flex', background: '#fff', borderBottom: '2px solid #e0e0e0', paddingLeft: 8, paddingTop: 6, overflowX: 'auto' }}>
        {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
          <button key={m} onClick={() => setMonth(m)} style={{
            padding: '7px 14px', border: 'none', borderBottom: m === month ? `3px solid ${LR_GREEN}` : '3px solid transparent',
            background: 'none', cursor: 'pointer', fontWeight: m === month ? 700 : 400,
            color: m === month ? LR_GREEN : '#555', fontSize: 13, whiteSpace: 'nowrap', flexShrink: 0,
          }}>
            {MONTH_NAMES[m]}
          </button>
        ))}
      </div>

      {/* Car legend */}
      {data.cars.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '7px 16px', background: '#fff', borderBottom: '1px solid #e0e0e0', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 700, fontSize: 12, color: '#666' }}>AUTA:</span>
          {data.cars.map(c => (
            <span key={c.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12 }}>
                <span style={{ width: 14, height: 14, borderRadius: 3, background: c.Color, border: '1px solid #ccc', display: 'inline-block' }} />
                {c.Title}
              </span>
              {c.Notes?.trim() && (
                <span style={{ fontSize: 11, fontWeight: 700, color: '#e74c3c', textAlign: 'center', maxWidth: 120 }}>{c.Notes}</span>
              )}
            </span>
          ))}
        </div>
      )}
      </div>{/* end topbar */}

      {/* Week blocks */}
      <div style={{ padding: '12px 8px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {data.weeks.map(wb => (
          <div key={`${wb.year}-${wb.week}`}>
            <div style={{ position: 'sticky', top: 'var(--sticky-top)', zIndex: 10, background: LR_GREEN, color: '#fff', padding: '5px 12px', fontWeight: 700, fontSize: 13, borderRadius: '6px 6px 0 0', display: 'flex', alignItems: 'center', gap: 12 }}>
              <span>Týden {wb.week}</span>
              <span style={{ fontWeight: 400, fontSize: 12, opacity: 0.85 }}>
                {fmtDate(wb.dates[0])} – {fmtDate(wb.dates[6])}
              </span>
              {wb.dates.includes(todayStr) && (
                <span style={{ fontSize: 11, background: 'rgba(255,255,255,0.25)', padding: '2px 8px', borderRadius: 10 }}>
                  aktuální
                </span>
              )}
            </div>
            <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', background: '#fff', boxShadow: '0 1px 6px rgba(0,0,0,0.07)' }}>
              <thead>
                <tr>
                  <th style={{ width: 160, minWidth: 160, background: '#f0f0f0', borderRight: '2px solid #ccc', borderBottom: '2px solid #ccc', padding: '4px 10px', textAlign: 'left', fontSize: 12, color: '#666' }}>
                    Pracovník
                  </th>
                  {wb.dates.map((ds, dayIdx) => {
                    const isWeekend = dayIdx >= 5;
                    const isToday = ds === todayStr;
                    return (
                      <th key={ds} style={{
                        width: 110, minWidth: 110,
                        background: isToday ? '#dff0c0' : isWeekend ? '#e8e8e8' : '#f0f0f0',
                        borderRight: '1px solid #ddd', borderBottom: '2px solid #ccc',
                        padding: '3px 4px', textAlign: 'center', fontSize: 11,
                        color: isToday ? '#4a7a1e' : '#444',
                        fontWeight: isToday ? 700 : isWeekend ? 400 : 600,
                      }}>
                        <div>{DAY_NAMES[dayIdx].slice(0, 2)}</div>
                        <div style={{ fontWeight: 400 }}>{fmtDate(ds)}</div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {data.workers.map((worker, rowIdx) => (
                  <tr key={worker.id} style={{ background: rowIdx % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={{ width: 160, minWidth: 160, padding: '4px 10px', fontSize: 13, fontWeight: 500, borderRight: '2px solid #ccc', borderBottom: '1px solid #eee', whiteSpace: 'nowrap' }}>
                      {worker.FullName || worker.Title}
                    </td>
                    {wb.dates.map((ds, dayIdx) => {
                      const isWeekend = dayIdx >= 5;
                      const isToday = ds === todayStr;
                      const wName = worker.FullName || worker.Title;
                      const entry = entryMap[`${wName}|${ds}`];
                      const car = entry?.CarId ? data.cars.find(c => Number(c.id) === entry.CarId) : undefined;
                      const bgColor = car?.Color || (isToday ? '#eef8dc' : isWeekend ? '#f0f0f0' : '#fff');
                      const textColor = car?.TextColor || '#222';
                      const lines = entry?.ProjectNames ? entry.ProjectNames.split(SEP).filter(Boolean) : [];

                      return (
                        <td key={ds} style={{ padding: 0, borderRight: '1px solid #ddd', borderBottom: '1px solid #eee', background: bgColor }}>
                          <div style={{ padding: '3px 6px', minHeight: 32 }}>
                            {lines.map((line, i) => (
                              <div key={i} style={{ fontSize: 11, color: textColor, lineHeight: '15px', whiteSpace: 'normal', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                                {line}
                              </div>
                            ))}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
